import { http, HttpResponse, delay } from 'msw';

import {
  buildMockScheduledTasks,
  SCHEDULED_TASK_USER_LIMIT,
} from '@/mocks/data/scheduledTaskData';
import { simpleEmployee } from '@/mocks/data/employeeData';
import { simpleUser } from '@/mocks/data/userData';
import {
  CreateScheduledTaskRequest,
  ScheduledTask,
  ScheduledTaskErrorCode,
  UpdateScheduledTaskRequest,
} from '@/types/scheduledTasks';

// In-memory store so create/edit/delete survive refetches until page reload.
let scheduledTasks = buildMockScheduledTasks();

// Same body the backend's shared SLAF error handler returns.
const errorResponse = (
  status: number,
  code: ScheduledTaskErrorCode,
  message: string
) =>
  HttpResponse.json(
    { detail: message, error: { code, message, details: null } },
    { status }
  );

// Mirrors the cron grammar the schedule builder can express; anything else
// is rejected the way the backend rejects an unsupported expression.
const MINUTE = '(?:[1-5]?\\d)';
const HOUR = '(?:2[0-3]|1\\d|\\d)';
const SUPPORTED_CADENCE_PATTERNS = [
  new RegExp(`^${MINUTE} ${HOUR} \\* \\* \\*$`),
  new RegExp(`^${MINUTE} ${HOUR} \\* \\* [0-6](?:,[0-6])*$`),
  new RegExp(`^${MINUTE} ${HOUR} (?:L|[1-9]|1\\d|2[0-8]) \\* \\*$`),
];

const WEEKDAY_LIST_PATTERN = /^[0-6](?:,[0-6])*$/;

// Weekday lists must be strictly ascending (sorted, no duplicates).
const hasAscendingWeekdays = (cadence: string) => {
  const weekdayField = cadence.split(' ')[4];
  if (!WEEKDAY_LIST_PATTERN.test(weekdayField)) return true;
  const weekdays = weekdayField.split(',').map(Number);
  return weekdays.every((day, i) => i === 0 || day > weekdays[i - 1]);
};

const isSupportedCadence = (cadence: unknown) =>
  typeof cadence === 'string' &&
  SUPPORTED_CADENCE_PATTERNS.some(pattern => pattern.test(cadence)) &&
  hasAscendingWeekdays(cadence);

const invalidCron = () =>
  errorResponse(
    400,
    ScheduledTaskErrorCode.InvalidCronExpression,
    'Cadence is not a supported schedule'
  );

const DEFAULT_TIMEZONE = 'UTC';

const isValidTimeZone = (timeZone: unknown) => {
  if (typeof timeZone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
};

const invalidTimezone = (message: string) =>
  errorResponse(400, ScheduledTaskErrorCode.InvalidTimezone, message);

const notFound = () =>
  errorResponse(
    404,
    ScheduledTaskErrorCode.ScheduledTaskNotFound,
    'Scheduled task not found'
  );

// Reset function for test isolation: the store otherwise outlives a test.
export const resetMockScheduledTasks = () => {
  scheduledTasks = buildMockScheduledTasks();
};

const ownTasks = () =>
  scheduledTasks.filter(task => task.user_id === simpleUser.id);

export const scheduledTaskHandlers = [
  // Admins list every task in the organization, whoever owns it.
  http.get('*/organizations/:organizationId/scheduled-tasks', async () => {
    await delay();
    return HttpResponse.json(scheduledTasks, { status: 200 });
  }),

  http.post(
    '*/organizations/:organizationId/users/me/scheduled-tasks',
    async ({ request, params }) => {
      const body = (await request.json()) as CreateScheduledTaskRequest;
      await delay();

      if (!isSupportedCadence(body.cadence)) {
        return invalidCron();
      }

      if (body.timezone !== undefined && !isValidTimeZone(body.timezone)) {
        return invalidTimezone(
          `'${body.timezone}' is not a valid IANA timezone`
        );
      }

      if (ownTasks().length >= SCHEDULED_TASK_USER_LIMIT) {
        return errorResponse(
          409,
          ScheduledTaskErrorCode.ScheduledTaskLimitExceeded,
          `Maximum of ${SCHEDULED_TASK_USER_LIMIT} scheduled tasks per user reached`
        );
      }

      const task: ScheduledTask = {
        organization_id: params.organizationId as string,
        user_id: simpleUser.id,
        schedule_id: crypto.randomUUID(),
        prompt: body.prompt,
        cadence: body.cadence,
        timezone: body.timezone ?? DEFAULT_TIMEZONE,
        cadence_description: body.cadence_description,
        enabled: true,
        disabled_reason: null,
        creator_email: simpleUser.email,
        recipient_employee_ids: body.recipient_employee_ids ?? [
          simpleEmployee.id,
        ],
        created_at: new Date().toISOString(),
        last_run_at: null,
        next_due_at: null,
        run_history: [],
      };
      scheduledTasks = [...scheduledTasks, task];
      return HttpResponse.json(task, { status: 201 });
    }
  ),

  http.patch(
    '*/organizations/:organizationId/scheduled-tasks/:scheduleId',
    async ({ request, params }) => {
      const body = (await request.json()) as UpdateScheduledTaskRequest;
      await delay();

      const existing = scheduledTasks.find(
        task => task.schedule_id === params.scheduleId
      );
      if (!existing) {
        return notFound();
      }

      if (body.cadence !== undefined && !isSupportedCadence(body.cadence)) {
        return invalidCron();
      }

      // As the backend does: a cadence is read in a zone, so it can't arrive
      // without one for a task that isn't in the default zone.
      if (
        body.cadence !== undefined &&
        body.timezone === undefined &&
        existing.timezone !== DEFAULT_TIMEZONE
      ) {
        return invalidTimezone(
          `This task runs in '${existing.timezone}'. Send the timezone together with a new cadence.`
        );
      }

      if (body.timezone !== undefined && !isValidTimeZone(body.timezone)) {
        return invalidTimezone(
          `'${body.timezone}' is not a valid IANA timezone`
        );
      }

      // Re-enabling clears the disabled reason, as the backend does.
      const updated: ScheduledTask = {
        ...existing,
        ...body,
        disabled_reason: body.enabled ? null : existing.disabled_reason,
      };
      scheduledTasks = scheduledTasks.map(task =>
        task.schedule_id === existing.schedule_id ? updated : task
      );
      return HttpResponse.json(updated, { status: 200 });
    }
  ),

  http.delete(
    '*/organizations/:organizationId/scheduled-tasks/:scheduleId',
    async ({ params }) => {
      await delay();

      if (
        !scheduledTasks.some(task => task.schedule_id === params.scheduleId)
      ) {
        return notFound();
      }
      scheduledTasks = scheduledTasks.filter(
        task => task.schedule_id !== params.scheduleId
      );
      return new HttpResponse(null, { status: 204 });
    }
  ),
];
